import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MenuesService } from '@services/menues/menues.service';
import { SignoSeguridadService } from '@services/seguridad/signo-seguridad.service';
import { StorageService } from '@services/shared/storage.service';
import { AuthService } from '@services/auth/auth.service';
import { TiposWebKeys } from '@enum/tiposweb-keys.enum';
import { DiccionarioKeys } from '@enum/idiomas-diccionario-keys.enum';
import { PopUpModalCustomService } from '@services/shared/popup-modal-custom.service';

interface TreeNode {
    _id: string;
    name: string;
    rawName: string;
    redirectTo: string;
    level: number;
    tipo: string;
    order: number;
    icon: string;
    expanded: boolean;
    visible: boolean;
    children: TreeNode[];
    parentId: string | null;
}

@Component({
    selector: 'app-menues-administracion',
    templateUrl: './menues-administracion.component.html',
    styleUrls: ['./menues-administracion.component.scss'],
})
export class MenuesAdministracionComponent implements OnInit, OnDestroy {

    treeRoots: TreeNode[] = [];
    flagLoaded: boolean = false;
    termino: string = '';
    allExpanded: boolean = false;

    menuToDelete: string | null = null;

    constructor(
        private router: Router,
        private menuesService: MenuesService,
        private signoSeguridadService: SignoSeguridadService,
        private storageService: StorageService,
        private authService: AuthService,
        private modalCustom: PopUpModalCustomService,
    ) {}

    ngOnDestroy(): void {
        this.modalCustom.cerrar();
    }

    async ngOnInit(): Promise<void> {
        if (!(await this.signoSeguridadService.puedeAccederMenues())) {
            this.signoSeguridadService.setAccesoDenegado(true);
            this.router.navigateByUrl('/configurar-administracion');
            return;
        }
        await this.cargarMenues();
    }

    private async cargarMenues(): Promise<void> {
        const resp = await this.menuesService.findAllMenues();
        const data = (resp?.response ?? resp?.data ?? resp?.docs ?? resp?.results ?? resp) as any[];
        const menus = Array.isArray(data) ? data : [];
        this.treeRoots = this.buildTree(menus);

        const translated = await this.cargarNombresTraducidos();
        this.aplicarNombresTraducidos(this.treeRoots, translated);

        this.flagLoaded = true;
    }

    private async cargarNombresTraducidos(): Promise<Map<string, string>> {
        const map = new Map<string, string>();
        const roots: any[] = await this.menuesService.findRootNodes(
            TiposWebKeys.WEB,
            DiccionarioKeys.I18NFILE,
            'es',
        );

        const rootIds: string[] = [];
        for (const r of roots) {
            map.set(r._id, r.name);
            rootIds.push(r._id);
        }

        await Promise.all(rootIds.map((id) => this.cargarDescRecursivo(id, map)));
        return map;
    }

    private async cargarDescRecursivo(parentId: string, map: Map<string, string>): Promise<void> {
        const children: any[] = await this.menuesService.getFindInmediateDescendants(
            parentId,
            DiccionarioKeys.I18NFILE,
            'es',
        );

        const childPromises = children.map(async (child: any) => {
            if (!map.has(child._id)) {
                map.set(child._id, this.limpiarNombre(child.name));
                await this.cargarDescRecursivo(child._id, map);
            }
        });

        await Promise.all(childPromises);
    }

    private aplicarNombresTraducidos(nodes: TreeNode[], map: Map<string, string>): void {
        for (const n of nodes) {
            if (map.has(n._id)) {
                n.name = map.get(n._id)!;
            }
            this.aplicarNombresTraducidos(n.children, map);
        }
    }

    private limpiarNombre(name: string): string {
        return name.replace(/general-service-signomedico\./g, '');
    }

    private buildTree(menus: any[]): TreeNode[] {
        const map = new Map<string, TreeNode>();

        for (const m of menus) {
            map.set(m._id, {
                _id: m._id,
                name: this.limpiarNombre(m.name || ''),
                rawName: m.name || '',
                redirectTo: m.redirectTo || '',
                level: m.level || 1,
                tipo: m.tipo || 'WEB',
                order: m.order || 0,
                icon: m.icon || '',
                expanded: false,
                visible: true,
                children: [],
                parentId: m.parent || null,
            });
        }

        const roots: TreeNode[] = [];
        for (const node of map.values()) {
            if (!node.parentId || !map.has(node.parentId)) {
                roots.push(node);
            } else {
                map.get(node.parentId)!.children.push(node);
            }
        }

        const sort = (n: TreeNode) => {
            n.children.sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
            n.children.forEach(sort);
        };
        roots.forEach(sort);
        roots.sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));

        return roots;
    }

    aplicarFiltro(termino: string): void {
        this.termino = termino;
        const q = (termino || '').trim().toLowerCase();

        if (!q) {
            const resetVisible = (nodes: TreeNode[]) => {
                for (const n of nodes) {
                    n.visible = true;
                    resetVisible(n.children);
                }
            };
            resetVisible(this.treeRoots);
            return;
        }

        const markVisible = (nodes: TreeNode[]): boolean => {
            let anyChild = false;
            for (const n of nodes) {
                const selfMatch = n.name.toLowerCase().includes(q) ||
                    n.redirectTo.toLowerCase().includes(q);
                const childMatch = markVisible(n.children);
                n.visible = selfMatch || childMatch;
                if (n.visible) { anyChild = true; }
            }
            return anyChild;
        };
        markVisible(this.treeRoots);
    }

    toggleExpand(node: TreeNode): void {
        node.expanded = !node.expanded;
    }

    toggleExpandAll(): void {
        if (this.allExpanded) {
            this.collapseAll();
        } else {
            this.expandAll();
        }
    }

    private expandAll(): void {
        const expand = (nodes: TreeNode[]) => {
            for (const n of nodes) {
                n.expanded = true;
                expand(n.children);
            }
        };
        expand(this.treeRoots);
        this.allExpanded = true;
    }

    private collapseAll(): void {
        const collapse = (nodes: TreeNode[]) => {
            for (const n of nodes) {
                n.expanded = false;
                collapse(n.children);
            }
        };
        collapse(this.treeRoots);
        this.allExpanded = false;
    }

    crearNuevo(): void {
        this.router.navigateByUrl('/menu-edicion');
    }

    editarMenu(menu: TreeNode): void {
        this.router.navigateByUrl(`/menu-edicion?id=${menu._id}`);
    }

    confirmarEliminar(menu: TreeNode): void {
        this.menuToDelete = menu._id;
        this.modalCustom.abrirPopup({
            titulo: 'Eliminar menú',
            mensaje: `¿Está seguro de eliminar "${menu.name}"? Esta acción puede afectar la navegación de usuarios con este permiso.`,
            tipo: 'warning',
            textoBoton: 'Eliminar',
            textoBotonCancelar: 'Cancelar',
            accionConfirmar: () => this.eliminarMenu(),
            accionCerrar: () => { this.menuToDelete = null; },
        });
    }

    async eliminarMenu(): Promise<void> {
        if (!this.menuToDelete) { return; }
        try {
            await this.menuesService.deleteMenu(this.menuToDelete);
            const user = await this.authService.getUserLogon();
            this.storageService.clearMenuCache('es', user?.perfil?._id || '');

            this.modalCustom.abrirPopup({
                titulo: 'Menú eliminado',
                mensaje: 'El menú se eliminó correctamente.',
                tipo: 'success',
            });

            await this.cargarMenues();
        } catch {
            this.modalCustom.abrirPopup({
                titulo: 'Error al eliminar',
                mensaje: 'Ocurrió un error al eliminar el menú. Intente nuevamente.',
                tipo: 'error',
            });
        }
        this.menuToDelete = null;
    }

    async cambiarOrden(node: TreeNode, delta: number): Promise<void> {
        const newOrder = (node.order || 1) + delta;
        if (newOrder < 1) { return; }
        await this.menuesService.updateMenu(node._id, {
            _id: node._id,
            order: newOrder,
            name: node.rawName,
            redirectTo: node.redirectTo,
            icon: node.icon || 'string',
            path: 'string',
            item: 'string',
        });
        node.order = newOrder;
        this.ordenarArbol(this.treeRoots);
    }

    private ordenarArbol(nodes: TreeNode[]): void {
        nodes.sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
        for (const n of nodes) {
            this.ordenarArbol(n.children);
        }
    }

    volver(): void {
        this.router.navigateByUrl('/menu-seguridad');
    }
}
